import React, { useState, useEffect } from "react";
import socket from "./WebSocketService";
import maleIcon from "./male-icon.png";
import femaleIcon from "./female-icon.png";
import { FaVolumeUp, FaSignOutAlt } from "react-icons/fa";

const Dashboard = () => {
    const [profiles, setProfiles] = useState([]);
    const [filteredProfiles, setFilteredProfiles] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedInstitute, setSelectedInstitute] = useState("All");
    const [institutes, setInstitutes] = useState([]);
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [chatHistory, setChatHistory] = useState([]);
    const [currentAudio, setCurrentAudio] = useState(null);

    useEffect(() => {
        socket.send({ command: "view_data" });

        socket.onMessage((event) => {
            const response = event;

            if (response.command === "response_view_data") {
                const data = response.data || [];
                setProfiles(data);
                setFilteredProfiles(data);

                // Extract unique institutes for the filter dropdown
                const uniqueInstitutes = ["All", ...new Set(data.map(profile => profile.institute))];
                setInstitutes(uniqueInstitutes);
            }

            if (response.command === "response_view_messages") {
                setChatHistory(response.data || []);
            }
        });
    }, []);

    const handleProfileClick = (profile) => {
        setSelectedProfile(profile);
        setChatHistory([]); // Clear chat while loading new messages
        socket.send({ command: "view_messages", enrollno: profile.enrollno, institute: profile.institute });
    };

    const handleLogout = () => {
        localStorage.removeItem("email");
        localStorage.removeItem("token");
        window.location.href = "/";
    };

    const playVoice = (base64Audio) => {
        // Stop any currently playing audio
        if (currentAudio) {
            currentAudio.pause();
            currentAudio.currentTime = 0;
        }

        // Create new audio
        const audio = new Audio(`data:audio/wav;base64,${base64Audio}`);
        audio.play();
        setCurrentAudio(audio);

        // Remove reference once audio ends
        audio.onended = () => setCurrentAudio(null);
    };

    // Filter logic
    useEffect(() => {
        let filtered = profiles;

        if (searchQuery) {
            filtered = filtered.filter(profile =>
                profile.name.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        if (selectedInstitute !== "All") {
            filtered = filtered.filter(profile => profile.institute === selectedInstitute);
        }

        setFilteredProfiles(filtered);
    }, [searchQuery, selectedInstitute, profiles]);

    return (
        <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
            <FaSignOutAlt
                onClick={handleLogout}
                style={{
                    position: "absolute",
                    top: "10px",
                    right: "10px",
                    fontSize: "24px",
                    color: "#d9534f",
                    cursor: "pointer",
                }}
                title="Logout"
            />
            {/* Left Side - Fixed Profile List */}
            <div style={{
                width: "300px",
                height: "100vh",
                borderRight: "1px solid #ddd",
                padding: "10px",
                overflowY: "auto",
                position: "fixed",
                left: 0,
                top: 0,
                backgroundColor: "#fff",
            }}>
                <h3>User Profiles</h3>

                {/* Search Input */}
                <input
                    type="text"
                    placeholder="Search by name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                        width: "100%",
                        padding: "8px",
                        marginBottom: "10px",
                        border: "1px solid #ccc",
                        borderRadius: "5px"
                    }}
                />

                {/* Filter Dropdown */}
                <select
                    value={selectedInstitute}
                    onChange={(e) => setSelectedInstitute(e.target.value)}
                    style={{
                        width: "100%",
                        padding: "8px",
                        marginBottom: "10px",
                        border: "1px solid #ccc",
                        borderRadius: "5px"
                    }}
                >
                    {institutes.map((inst, index) => (
                        <option key={index} value={inst}>{inst}</option>
                    ))}
                </select>

                <div style={{ display: "grid", gap: "10px" }}>
                    {filteredProfiles.map((profile, index) => (
                        <div
                            key={index}
                            onClick={() => handleProfileClick(profile)}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                border: "1px solid #ddd",
                                padding: "10px",
                                cursor: "pointer",
                                backgroundColor: selectedProfile?.enrollno === profile.enrollno ? "#eef" : "white",
                            }}
                        >
                            <img
                                src={profile.gender === "male" ? maleIcon : femaleIcon}
                                alt="gender icon"
                                style={{ width: "40px", height: "40px", marginRight: "10px" }}
                            />
                            <div>
                                <p style={{ margin: "0", fontWeight: "bold" }}>{profile.name}</p>
                                <p style={{ margin: "0", fontSize: "12px", color: "#666" }}>{profile.institute}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Right Side - Chat Section */}
            <div style={{
                marginLeft: "300px",
                flex: 1,
                padding: "20px",
                display: "flex",
                flexDirection: "column",
                overflowY: "hidden"
            }}>
                {selectedProfile ? (
                    <>
                        {/* Selected Profile Info */}
                        <div style={{ borderBottom: "1px solid #ddd", paddingBottom: "10px", marginBottom: "10px" }}>
                            <p>Chat with {selectedProfile.name}</p>
                            <p><b>Institute:</b> {selectedProfile.institute}</p>
                            <p><b>Enroll No:</b> {selectedProfile.enrollno}</p>
                        </div>

                        {/* Chat Messages */}
                        <div style={{ flex: 1, overflowY: "auto", border: "1px solid #ddd", padding: "10px" }}>
                            {chatHistory.length > 0 ? (
                                chatHistory.map((msg, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            textAlign: msg.from === "user" ? "right" : "left",
                                            marginBottom: "10px",
                                        }}
                                    >
                                        <p style={{
                                            display: "inline-block",
                                            padding: "10px",
                                            borderRadius: "10px",
                                            backgroundColor: msg.from === "user" ? "#d1e7fd" : "#f1f1f1",
                                            maxWidth: "70%",
                                        }}>
                                            {msg.message}
                                        </p>
                                        {msg.voice && (
                                            <FaVolumeUp
                                                onClick={() => playVoice(msg.voice)}
                                                style={{ marginLeft: "10px", cursor: "pointer", fontSize: "20px", color: "#007bff" }}
                                            />
                                        )}
                                        <p style={{ fontSize: "12px", color: "#666" }}>{msg.time}</p>
                                    </div>
                                ))
                            ) : (
                                <p style={{ color: "#888" }}>No messages yet.</p>
                            )}
                        </div>
                    </>
                ) : (
                    <p style={{ color: "#888" }}>Select a profile to start chat</p>
                )}
            </div>
        </div>
    );
};

export default Dashboard;
